import React, { useState, useEffect } from "react";
import {
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Badge,
  Box,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { Close, Search, Visibility, FilterList } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { getAllCandidates, getB2bSkills, getCityOptions, getCourseOptions, getDegreeOptions, recommendJobToCandidates } from "../../redux/actions/b2bActions";
import { buildCandidateProfilePath } from "../../constants/routes";

interface Candidate {
  id?: number;
  user_id?: number;
  userId?: number;
  name?: string;
  fullName?: string;
  full_name?: string;
  email?: string;
  profilePicturePath?: string;
  profile_picture_path?: string;
  isRecommended?: boolean;
  isrecommended?: boolean;
  skills?: string[];
  qualification?: string[];
  city?: string;
  userDetail?: { city?: string };
  profileDetail?: { skills?: string[] };
  education?: Array<{ qualification?: string; courseName?: string }>;
  courses?: string[];
}

interface RecommendJobModalProps {
  open: boolean;
  onClose: () => void;
  jobId: number;
  jobTitle?: string;
  /** Pre-select these candidate user IDs when opening (e.g. from confirmation flow) */
  initialSelectedCandidateIds?: number[];
}

const RecommendJobModal: React.FC<RecommendJobModalProps> = ({ open, onClose, jobId, jobTitle, initialSelectedCandidateIds }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSkill, setFilterSkill] = useState<string>("");
  const [filterDegree, setFilterDegree] = useState<string>("");
  const [filterCourse, setFilterCourse] = useState<string>("");
  const [filterCity, setFilterCity] = useState<string>("");
  const [appliedFilterSkill, setAppliedFilterSkill] = useState<string>("");
  const [appliedFilterDegree, setAppliedFilterDegree] = useState<string>("");
  const [appliedFilterCourse, setAppliedFilterCourse] = useState<string>("");
  const [appliedFilterCity, setAppliedFilterCity] = useState<string>("");
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);
  const [skillsFromApi, setSkillsFromApi] = useState<string[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [qualificationsFromApi, setQualificationsFromApi] = useState<string[]>([]);
  const [qualificationsLoading, setQualificationsLoading] = useState(false);
  const [coursesFromApi, setCoursesFromApi] = useState<string[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [citiesFromApi, setCitiesFromApi] = useState<string[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [selectedCandidateForProfile, setSelectedCandidateForProfile] = useState<Candidate | null>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setFilterSkill("");
      setFilterDegree("");
      setFilterCourse("");
      setFilterCity("");
      setAppliedFilterSkill("");
      setAppliedFilterDegree("");
      setAppliedFilterCourse("");
      setAppliedFilterCity("");
      setFilterPopupOpen(false);
      setError("");
      fetchCandidates(null); // Fetch without filters on open
    } else {
      setSelectedCandidates([]);
    }
  }, [open, jobId]);

  // When filter popup opens, sync dropdown values with applied filters
  useEffect(() => {
    if (filterPopupOpen) {
      setFilterSkill(appliedFilterSkill);
      setFilterDegree(appliedFilterDegree);
      setFilterCourse(appliedFilterCourse);
      setFilterCity(appliedFilterCity);
    }
  }, [filterPopupOpen, appliedFilterSkill, appliedFilterDegree, appliedFilterCourse, appliedFilterCity]);

  // Fetch B2B skills and degree qualifications when filter popup opens
  const filterDataFetchedRef = React.useRef(false);
  useEffect(() => {
    if (filterPopupOpen && !filterDataFetchedRef.current) {
      filterDataFetchedRef.current = true;
      setSkillsLoading(true);
      setQualificationsLoading(true);
      setCitiesLoading(true);
      dispatch(
        getB2bSkills(
          (skills: string[]) => {
            setSkillsFromApi(skills);
            setSkillsLoading(false);
          },
          () => setSkillsLoading(false)
        )
      );
      dispatch(
        getDegreeOptions(
          (qualifications: string[]) => {
            setQualificationsFromApi(qualifications);
            setQualificationsLoading(false);
          },
          () => setQualificationsLoading(false)
        )
      );
      dispatch(
        getCityOptions(
          (cities: string[]) => {
            setCitiesFromApi(cities);
            setCitiesLoading(false);
          },
          () => setCitiesLoading(false)
        )
      );
    }
    if (!filterPopupOpen) {
      filterDataFetchedRef.current = false;
    }
  }, [filterPopupOpen, dispatch]);

  // Fetch courses when degree is selected
  useEffect(() => {
    if (filterDegree) {
      setCoursesLoading(true);
      setFilterCourse("");
      dispatch(
        getCourseOptions(
          filterDegree,
          (courses: string[]) => {
            setCoursesFromApi(courses);
            setCoursesLoading(false);
          },
          () => setCoursesLoading(false)
        )
      );
    } else {
      setCoursesFromApi([]);
      setFilterCourse("");
    }
  }, [filterDegree, dispatch]);

  // Fetch when search changes ONLY if no filters applied; with filters, search is local
  const isInitialOpen = React.useRef(true);
  useEffect(() => {
    if (!open) {
      isInitialOpen.current = true;
      return;
    }
    if (isInitialOpen.current) {
      isInitialOpen.current = false;
      return;
    }
    const hasFilters =
      appliedFilterSkill || appliedFilterDegree || appliedFilterCourse || appliedFilterCity;
    if (hasFilters) {
      return; // Local search when filters applied, no API call
    }
    const timer = setTimeout(() => {
      fetchCandidates();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, open, jobId, appliedFilterSkill, appliedFilterDegree, appliedFilterCourse, appliedFilterCity]);

  const fetchCandidates = (
    overrideFilters?: { skills?: string; degree?: string; course?: string; city?: string } | null
  ) => {
    setLoading(true);
    const filters =
      overrideFilters !== undefined
        ? overrideFilters === null
          ? undefined
          : overrideFilters && Object.keys(overrideFilters).length > 0
            ? overrideFilters
            : undefined
        : appliedFilterSkill || appliedFilterDegree || appliedFilterCourse || appliedFilterCity
          ? {
              ...(appliedFilterSkill && { skills: appliedFilterSkill }),
              ...(appliedFilterDegree && { degree: appliedFilterDegree }),
              ...(appliedFilterCourse && { course: appliedFilterCourse }),
              ...(appliedFilterCity && { city: appliedFilterCity }),
            }
          : undefined;
    dispatch(
      getAllCandidates(
        jobId,
        searchQuery,
        filters,
        (candidatesList: Candidate[] | any) => {
          setError("");
          let raw: any[] = [];
          if (Array.isArray(candidatesList)) {
            raw = candidatesList;
          } else if (candidatesList?.result?.records && Array.isArray(candidatesList.result.records)) {
            raw = candidatesList.result.records;
          } else if (candidatesList?.records && Array.isArray(candidatesList.records)) {
            raw = candidatesList.records;
          }
          const list = raw.filter((c: any) => c && typeof c === "object");
          setCandidates(list);
          // Pre-select candidates that are already recommended, plus any initial IDs (e.g. from confirmation)
          const recommendedCandidateIds = list
            .filter((candidate: Candidate) => candidate.isRecommended || candidate.isrecommended)
            .map((candidate: Candidate) => getCandidateId(candidate));
          const withInitial = Array.from(
            new Set([...recommendedCandidateIds, ...(initialSelectedCandidateIds || [])])
          );
          if (withInitial.length > 0) {
            setSelectedCandidates(withInitial);
          }
          setLoading(false);
        },
        (err: any) => {
          setError("Failed to load candidates. Please try again.");
          setLoading(false);
        }
      )
    );
  };

  const getCandidateId = (candidate: Candidate): number => {
    return candidate.userId ?? candidate.user_id ?? candidate.id ?? 0;
  };

  const getCandidateName = (candidate: Candidate): string => {
    return candidate.fullName || candidate.full_name || candidate.name || "Unknown";
  };

  const getCandidateEmail = (candidate: Candidate): string => {
    return candidate.email || "";
  };

  const getCandidateProfilePic = (candidate: Candidate): string | undefined => {
    return candidate.profilePicturePath || candidate.profile_picture_path;
  };

  const isAlreadyRecommended = (candidate: Candidate) =>
    Boolean(candidate.isRecommended || candidate.isrecommended);

  const getCandidateSkills = (c: Candidate): string[] => {
    const s = c.skills ?? c.profileDetail?.skills ?? [];
    return Array.isArray(s) ? s : s ? [String(s)] : [];
  };

  const getCandidateQualification = (c: Candidate): string[] => {
    const q = c.qualification ?? c.courses ?? c.education?.map((e) => e.qualification ?? e.courseName).filter((x): x is string => Boolean(x)) ?? [];
    return Array.isArray(q) ? q : q ? [String(q)] : [];
  };

  const getCandidateCity = (c: Candidate): string => {
    return c.city ?? c.userDetail?.city ?? "";
  };

  const handleToggleCandidate = (candidate: Candidate, event?: React.MouseEvent) => {
    if (isAlreadyRecommended(candidate)) return;
    if (event) {
      event.stopPropagation();
    }
    const candidateId = getCandidateId(candidate);
    setSelectedCandidates((prev) =>
      prev.includes(candidateId) ? prev.filter((id) => id !== candidateId) : [...prev, candidateId]
    );
  };

  const handleViewProfile = (candidate: Candidate, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedCandidateForProfile(candidate);
    setProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setProfileModalOpen(false);
    setSelectedCandidateForProfile(null);
  };

  // With filters: local search by name/email. Without filters: API handles search, use candidates.
  const filteredCandidates = React.useMemo(() => {
    if (!searchQuery.trim()) return candidates;
    const hasFilters =
      appliedFilterSkill || appliedFilterDegree || appliedFilterCourse || appliedFilterCity;
    if (!hasFilters) return candidates; // API returns search results
    const q = searchQuery.trim().toLowerCase();
    return candidates.filter((c) => {
      const name = (getCandidateName(c) || "").toLowerCase();
      const email = (getCandidateEmail(c) || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [candidates, searchQuery, appliedFilterSkill, appliedFilterDegree, appliedFilterCourse, appliedFilterCity]);

  const hasActiveFilters = Boolean(
    appliedFilterSkill || appliedFilterDegree || appliedFilterCourse || appliedFilterCity
  );

  const handleApplyFilters = () => {
    setAppliedFilterSkill(filterSkill);
    setAppliedFilterDegree(filterDegree);
    setAppliedFilterCourse(filterCourse);
    setAppliedFilterCity(filterCity);
    fetchCandidates({
      ...(filterSkill && { skills: filterSkill }),
      ...(filterDegree && { degree: filterDegree }),
      ...(filterCourse && { course: filterCourse }),
      ...(filterCity && { city: filterCity }),
    });
    setFilterPopupOpen(false);
  };

  const handleClearFilters = () => {
    setFilterSkill("");
    setFilterDegree("");
    setFilterCourse("");
    setFilterCity("");
    setAppliedFilterSkill("");
    setAppliedFilterDegree("");
    setAppliedFilterCourse("");
    setAppliedFilterCity("");
    fetchCandidates(null);
    setFilterPopupOpen(false);
  };

  const selectableCandidates = filteredCandidates.filter((c) => !isAlreadyRecommended(c));
  const selectableIds = selectableCandidates.map((c) => getCandidateId(c));
  const selectedSelectableCount = selectableIds.filter((id) => selectedCandidates.includes(id)).length;

  const handleSelectAll = () => {
    if (selectedSelectableCount === selectableIds.length) {
      setSelectedCandidates((prev) => prev.filter((id) => !selectableIds.includes(id)));
    } else {
      setSelectedCandidates((prev) => {
        const recommendedIds = prev.filter((id) => !selectableIds.includes(id));
        return Array.from(new Set([...recommendedIds, ...selectableIds]));
      });
    }
  };

  const handleRecommend = () => {
    const toRecommendIds = selectedCandidates.filter((id) => selectableIds.includes(id));
    if (toRecommendIds.length === 0) {
      setError("Please select at least one candidate to recommend");
      return;
    }

    setSubmitting(true);
    setError("");
    dispatch(
      recommendJobToCandidates(
        jobId,
        toRecommendIds,
        () => {
          setError("");
          setSubmitting(false);
          onClose();
        },
        (err: any) => {
          setError("Failed to recommend job. Please try again.");
          setSubmitting(false);
        }
      )
    );
  };

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "500px",
          maxWidth: "100%",
          height: isMobile ? "90vh" : "100vh",
          borderTopLeftRadius: isMobile ? "20px" : 0,
          borderTopRightRadius: isMobile ? "20px" : 0,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fff",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1F2235", fontSize: "18px" }}>
              Recommend Job to Candidates
            </Typography>
            {jobTitle && (
              <Typography variant="body2" sx={{ color: "#666", mt: 0.5, fontSize: "13px" }}>
                {jobTitle}
              </Typography>
            )}
          </Box>
          <IconButton 
            onClick={onClose} 
            size="small" 
            sx={{ 
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", padding: "20px 24px", backgroundColor: "#fafafa" }}>
          <Box sx={{ marginBottom: "20px", display: "flex", gap: "12px", alignItems: "center" }}>
            <TextField
              fullWidth
              placeholder="Search by name and email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#756DE9",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#756DE9",
                  },
                },
              }}
            />
            <Badge
              variant="dot"
              invisible={!hasActiveFilters}
              sx={{
                "& .MuiBadge-dot": {
                  right: 4,
                  top: 4,
                  backgroundColor: "#756DE9",
                },
              }}
            >
              <IconButton
                onClick={() => setFilterPopupOpen(true)}
                size="small"
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  color: hasActiveFilters ? "#756DE9" : "#999",
                  "&:hover": {
                    backgroundColor: "#f9f9f9",
                    borderColor: "#756DE9",
                    color: "#756DE9",
                  },
                }}
                title="Filter by degree, course, skills, city"
              >
                <FilterList fontSize="small" />
              </IconButton>
            </Badge>
          </Box>

          <Dialog
            open={filterPopupOpen}
            onClose={() => setFilterPopupOpen(false)}
            PaperProps={{
              sx: {
                borderRadius: "16px",
                m: 2,
                width: "100%",
                maxWidth: "90vw",
                height: "auto",
                maxHeight: "90vh",
                boxShadow: "0 24px 48px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.08)",
                overflow: "hidden",
                [theme.breakpoints.up("sm")]: {
                  width: 400,
                  minWidth: 400,
                  maxWidth: "90vw",
                },
                [theme.breakpoints.up("md")]: {
                  width: 440,
                  minWidth: 440,
                },
                [theme.breakpoints.up("lg")]: {
                  width: 480,
                  minWidth: 480,
                },
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 3,
                py: 2,
                backgroundColor: "#f8f7ff",
                borderBottom: "1px solid #e8e6f5",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1F2235", fontSize: "1.125rem" }}>
                Filter candidates
              </Typography>
              <IconButton
                size="small"
                onClick={() => setFilterPopupOpen(false)}
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "#efeef8",
                    color: "#756DE9",
                  },
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 4, py: 4, pt: 4, backgroundColor: "#fff", overflow: "visible" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, overflow: "visible" }}>
                <Autocomplete
                  fullWidth
                  options={qualificationsFromApi}
                  value={filterDegree || null}
                  onChange={(_, newValue) => setFilterDegree(newValue || "")}
                  loading={qualificationsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Degree"
                      size="small"
                      placeholder="Select degree"
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: true,
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {qualificationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  componentsProps={{
                    popper: {
                      sx: {
                        "& .MuiAutocomplete-paper": {
                          borderRadius: "10px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          mt: 1,
                        },
                        "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
                          py: 1.25,
                          "&:hover": { backgroundColor: "#f8f7ff" },
                          "&[aria-selected=true]": { backgroundColor: "#efeef8" },
                        },
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fafafa",
                      "& fieldset": { borderColor: "#e8e6f5" },
                      "&:hover fieldset": { borderColor: "#756DE9", backgroundColor: "#fff" },
                      "&.Mui-focused fieldset": { borderColor: "#756DE9", borderWidth: "2px", backgroundColor: "#fff" },
                    },
                    "& .MuiInputLabel-outlined": {
                      whiteSpace: "nowrap",
                      overflow: "visible",
                    },
                  }}
                />
                {filterDegree && (
                  <Autocomplete
                    options={coursesFromApi}
                    value={filterCourse || null}
                    onChange={(_, newValue) => setFilterCourse(newValue || "")}
                    loading={coursesLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Course"
                        size="small"
                        placeholder="Select course"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {coursesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    componentsProps={{
                      popper: {
                        sx: {
                          "& .MuiAutocomplete-paper": {
                            borderRadius: "10px",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            mt: 1,
                          },
                          "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
                            py: 1.25,
                            "&:hover": { backgroundColor: "#f8f7ff" },
                            "&[aria-selected=true]": { backgroundColor: "#efeef8" },
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "#fafafa",
                        "& fieldset": { borderColor: "#e8e6f5" },
                        "&:hover fieldset": { borderColor: "#756DE9", backgroundColor: "#fff" },
                        "&.Mui-focused fieldset": { borderColor: "#756DE9", borderWidth: "2px", backgroundColor: "#fff" },
                      },
                    }}
                  />
                )}
                <Autocomplete
                  options={skillsFromApi}
                  value={filterSkill || null}
                  onChange={(_, newValue) => setFilterSkill(newValue || "")}
                  loading={skillsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Skills"
                      size="small"
                      placeholder="Select skill"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {skillsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  componentsProps={{
                    popper: {
                      sx: {
                        "& .MuiAutocomplete-paper": {
                          borderRadius: "10px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          mt: 1,
                        },
                        "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
                          py: 1.25,
                          "&:hover": { backgroundColor: "#f8f7ff" },
                          "&[aria-selected=true]": { backgroundColor: "#efeef8" },
                        },
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fafafa",
                      "& fieldset": { borderColor: "#e8e6f5" },
                      "&:hover fieldset": { borderColor: "#756DE9", backgroundColor: "#fff" },
                      "&.Mui-focused fieldset": { borderColor: "#756DE9", borderWidth: "2px", backgroundColor: "#fff" },
                    },
                  }}
                />
                <Autocomplete
                  fullWidth
                  options={citiesFromApi}
                  value={filterCity || null}
                  onChange={(_, newValue) => setFilterCity(newValue || "")}
                  loading={citiesLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      size="small"
                      placeholder="Select city"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {citiesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  componentsProps={{
                    popper: {
                      sx: {
                        "& .MuiAutocomplete-paper": {
                          borderRadius: "10px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          mt: 1,
                        },
                        "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
                          py: 1.25,
                          "&:hover": { backgroundColor: "#f8f7ff" },
                          "&[aria-selected=true]": { backgroundColor: "#efeef8" },
                        },
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fafafa",
                      "& fieldset": { borderColor: "#e8e6f5" },
                      "&:hover fieldset": { borderColor: "#756DE9", backgroundColor: "#fff" },
                      "&.Mui-focused fieldset": { borderColor: "#756DE9", borderWidth: "2px", backgroundColor: "#fff" },
                    },
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                px: 4,
                py: 2,
                gap: 2,
                borderTop: "1px solid #e8e6f5",
                backgroundColor: "#fafafa",
              }}
            >
              <Button
                onClick={handleClearFilters}
                variant="outlined"
                sx={{
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                Clear
              </Button>
              <Button
                onClick={handleApplyFilters}
                variant="contained"
                sx={{
                  backgroundColor: "#756DE9",
                  color: "#fff",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#5a4dbf",
                  },
                }}
              >
                Apply
              </Button>
            </DialogActions>
          </Dialog>

          {error && (
            <Box sx={{ marginBottom: "16px", padding: "12px 16px", backgroundColor: "#ffebee", borderRadius: "8px", border: "1px solid #ef5350" }}>
              <Typography variant="body2" sx={{ color: "#c62828" }}>
                {error}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              backgroundColor: "#fff",
              overflow: "hidden",
              maxHeight: "calc(100vh - 300px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", padding: "60px 40px" }}>
                <CircularProgress size={32} sx={{ color: "#756DE9" }} />
              </Box>
            ) : filteredCandidates.length === 0 ? (
              <Box sx={{ padding: "60px 40px", textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#999", fontSize: "14px" }}>
                  {candidates.length === 0 ? "No candidates found" : "No candidates match the selected filters"}
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    padding: "14px 16px",
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: "#f9f9f9",
                    display: "flex",
                    alignItems: "center",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <Checkbox
                    checked={selectableIds.length > 0 && selectedSelectableCount === selectableIds.length}
                    indeterminate={selectedSelectableCount > 0 && selectedSelectableCount < selectableIds.length}
                    onChange={handleSelectAll}
                    sx={{
                      color: "#756DE9",
                      "&.Mui-checked": {
                        color: "#756DE9",
                      },
                    }}
                  />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1F2235", fontSize: "14px" }}>
                      Select All ({selectableIds.length})
                  </Typography>
                </Box>
                <Box sx={{ overflowY: "auto", flex: 1 }}>
                  <List sx={{ padding: 0 }}>
                {filteredCandidates.map((candidate, index) => {
                  const candidateId = getCandidateId(candidate);
                  const candidateName = getCandidateName(candidate);
                  const candidateEmail = getCandidateEmail(candidate);
                  const profilePic = getCandidateProfilePic(candidate);
                  const listKey = candidateId || `c-${index}`;
                  const alreadyRecommended = isAlreadyRecommended(candidate);

                  return (
                    <ListItem
                      key={listKey}
                      disablePadding
                      sx={{
                        borderBottom: "1px solid #f0f0f0",
                        opacity: alreadyRecommended ? 0.7 : 1,
                        "&:hover": {
                          backgroundColor: alreadyRecommended ? "transparent" : "#f9f9f9",
                        },
                      }}
                    >
                    <ListItemButton 
                      onClick={(e) => handleToggleCandidate(candidate, e)}
                      disabled={alreadyRecommended}
                      sx={{ paddingRight: "8px" }}
                    >
                      <Checkbox
                        checked={selectedCandidates.includes(candidateId)}
                        disabled={alreadyRecommended}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!alreadyRecommended) handleToggleCandidate(candidate);
                        }}
                        sx={{
                          color: "#756DE9",
                          "&.Mui-checked": {
                            color: "#756DE9",
                          },
                          "&.Mui-disabled": {
                            color: "#756DE9",
                            opacity: 0.8,
                          },
                        }}
                      />
                      <ListItemAvatar>
                        <Avatar
                          src={profilePic || "/images/browse-jobs/applicant_profile.svg"}
                          alt={candidateName}
                          sx={{ 
                            width: 48, 
                            height: 48, 
                            bgcolor: "#756DE9",
                          }}
                          imgProps={{
                            style: { objectFit: "cover" },
                          }}
                        >
                          {candidateName?.charAt(0)?.toUpperCase() || "?"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={candidateName}
                        secondary={alreadyRecommended ? "Already recommended" : (candidateEmail || "No email provided")}
                        primaryTypographyProps={{
                          sx: { fontWeight: 500, color: "#1F2235", fontSize: "14px" },
                        }}
                        secondaryTypographyProps={{
                          sx: { color: alreadyRecommended ? "#756DE9" : "#666", fontSize: "13px", mt: 0.5 },
                        }}
                      />
                      <IconButton
                        onClick={(e) => handleViewProfile(candidate, e)}
                        size="small"
                        sx={{
                          color: "#756DE9",
                          "&:hover": {
                            backgroundColor: "#f0edff",
                          },
                        }}
                        title="View Profile"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                    </ListItem>
                  );
                  })}
                  </List>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            padding: "20px 24px",
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: "#666",
              borderColor: "#e0e0e0",
              borderRadius: "8px",
              padding: "10px 24px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#999",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRecommend}
            variant="contained"
            disabled={selectedSelectableCount === 0 || submitting}
            sx={{
              backgroundColor: "#756DE9",
              color: "#fff",
              borderRadius: "8px",
              padding: "10px 24px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#5a4dbf",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
                color: "#fff",
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Recommend Job"
            )}
          </Button>
        </Box>
      </Box>

      {/* Candidate Profile Modal */}
      {selectedCandidateForProfile && (
        <Dialog
          open={profileModalOpen}
          onClose={handleCloseProfileModal}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              maxHeight: "90vh",
              height: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 24px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1F2235" }}>
              Candidate Profile
            </Typography>
            <IconButton 
              onClick={handleCloseProfileModal} 
              size="small" 
              sx={{ 
                color: "#666",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ padding: 0, height: "100%", overflow: "hidden" }}>
            <Box sx={{ height: "100%", width: "100%" }}>
              <iframe
                src={`${window.location.origin}${buildCandidateProfilePath(getCandidateId(selectedCandidateForProfile), getCandidateName(selectedCandidateForProfile))}`}
                style={{
                  width: "100%",
                  height: "calc(90vh - 64px)",
                  border: "none",
                }}
                title="Candidate Profile"
              />
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Drawer>
  );
};

export default RecommendJobModal;
